import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatDate, formatDateTime } from "@/lib/statuts";
import { useStatuts } from "@/context/StatutsContext";
import { StatutBadge } from "@/components/StatutBadge";
import { Search, SlidersHorizontal, Plus, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, FileText, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import VisiteFormModal from "@/components/VisiteFormModal";
import { DatePicker } from "@/components/DatePicker";

const COLS = [
  { key: "numero_dossier", label: "N° dossier", type: "num" },
  { key: "date_creation", label: "Date création", type: "date" },
  { key: "client_nom", label: "Nom complet", type: "str" },
  { key: "client_telephone", label: "Téléphone", type: "str" },
  { key: "code_postal", label: "CP", type: "str" },
  { key: "date_visite", label: "Date visite", type: "date" },
  { key: "technicien_nom", label: "Technicien", type: "str" },
  { key: "statut_label", label: "Statut", type: "str" },
  { key: "type_visite", label: "Type de visite", type: "str" },
  { key: "origine", label: "Origine", type: "str" },
  { key: "nb_documents", label: "Docs", type: "num" },
  { key: "createur_nom", label: "Créateur", type: "str" },
  { key: "unite_affaire", label: "Unité d'affaire", type: "str" },
];

function cmp(a, b, key, type, dir) {
  let va = a[key], vb = b[key];
  if (type === "num") { va = parseFloat(va) || 0; vb = parseFloat(vb) || 0; return (va - vb) * dir; }
  va = (va || "").toString(); vb = (vb || "").toString();
  if (type === "date") { return (va < vb ? -1 : va > vb ? 1 : 0) * dir; }
  return va.localeCompare(vb, "fr", { numeric: true }) * dir;
}

const EMPTY_FILTERS = { statut: "", technicien_id: "", installateur_id: "", type_visite: "", origine: "", code_postal: "", ville: "", date_visite_from: "", date_visite_to: "" };

export default function RendezVous() {
  const { user } = useAuth();
  const { statuts } = useStatuts();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [allRows, setAllRows] = useState([]);
  const [page, setPage] = useState(1);
  const limit = 25;
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(sp.get("q") || "");
  const [sort, setSort] = useState({ by: "date_creation", dir: -1 });
  const [showFilters, setShowFilters] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [techs, setTechs] = useState([]);
  const [ents, setEnts] = useState([]);
  const [types, setTypes] = useState([]);
  const canCreate = ["admin", "planification", "installateur"].includes(user.role);
  const canFilterInternal = ["admin", "planification"].includes(user.role);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: 1, limit: 1000, sort_by: "date_creation", sort_dir: -1 });
    if (search) params.set("search", search);
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    try {
      const { data } = await api.get(`/visites?${params}`);
      setAllRows(data.items);
    } finally {
      setLoading(false);
    }
  }, [search, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, filters, sort]);

  useEffect(() => {
    api.get("/meta").then((r) => setTypes(r.data.types_visite)).catch(() => {});
    if (canFilterInternal) {
      api.get("/users/techniciens").then((r) => setTechs(r.data)).catch(() => {});
      api.get("/entreprises").then((r) => setEnts(r.data)).catch(() => {});
    }
  }, [canFilterInternal]);

  const sortedRows = useMemo(() => {
    const col = COLS.find((c) => c.key === sort.by) || { type: "str" };
    return [...allRows].sort((a, b) => cmp(a, b, sort.by, col.type, sort.dir));
  }, [allRows, sort]);

  const total = sortedRows.length;
  const pages = Math.max(1, Math.ceil(total / limit));
  const rows = sortedRows.slice((page - 1) * limit, page * limit);

  const toggleSort = (key) => setSort((s) => ({ by: key, dir: s.by === key ? -s.dir : 1 }));
  const activeFilters = Object.values(filters).filter(Boolean).length;

  const SortIcon = ({ k }) => sort.by !== k ? <ArrowUpDown className="h-3 w-3 opacity-40" /> : sort.dir === 1 ? <ArrowUp className="h-3 w-3 text-blue-600" /> : <ArrowDown className="h-3 w-3 text-blue-600" />;
  const cols = canFilterInternal ? COLS : COLS.filter((c) => c.key !== "origine");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">
            {user.role === "technicien" ? "Mes rendez-vous" : user.role === "installateur" ? "Mes dossiers" : "Rendez-vous"}
          </h1>
          <p className="text-sm text-slate-500">{total} élément{total > 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="rdv-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher…"
              className="w-52 rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-slate-400"
            />
          </div>
          <button
            data-testid="toggle-filters"
            onClick={() => setShowFilters((s) => !s)}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <SlidersHorizontal className="h-4 w-4" /> Filtres
            {activeFilters > 0 && <span className="rounded-full bg-blue-600 px-1.5 text-[10px] font-bold text-white">{activeFilters}</span>}
          </button>
          {canCreate && (
            <button data-testid="rdv-add" onClick={() => setShowForm(true)} className="flex items-center gap-2 rounded-lg bg-[#0A0A0A] px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800">
              <Plus className="h-4 w-4" /> Ajouter
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div data-testid="filters-panel" className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-3 lg:grid-cols-4">
          <FilterSelect label="Statut" value={filters.statut} onChange={(v) => setFilters({ ...filters, statut: v })} options={statuts.map((s) => ({ v: s.code, l: s.label }))} testid="filter-statut" />
          <FilterSelect label="Type de visite" value={filters.type_visite} onChange={(v) => setFilters({ ...filters, type_visite: v })} options={types.map((t) => ({ v: t, l: t }))} testid="filter-type" />
          {canFilterInternal && <FilterSelect label="Origine" value={filters.origine} onChange={(v) => setFilters({ ...filters, origine: v })} options={[{ v: "PARTENAIRE", l: "Partenaire" }, { v: "NOVASOL", l: "NovaSol" }]} testid="filter-origine" />}
          {canFilterInternal && <FilterSelect label="Technicien" value={filters.technicien_id} onChange={(v) => setFilters({ ...filters, technicien_id: v })} options={techs.map((t) => ({ v: t.id, l: `${t.prenom} ${t.nom}` }))} testid="filter-technicien" />}
          {canFilterInternal && <FilterSelect label="Installateur" value={filters.installateur_id} onChange={(v) => setFilters({ ...filters, installateur_id: v })} options={ents.map((e) => ({ v: e.id, l: e.raison_sociale }))} testid="filter-installateur" />}
          <FilterInput label="Code postal" value={filters.code_postal} onChange={(v) => setFilters({ ...filters, code_postal: v })} testid="filter-cp" />
          <FilterInput label="Ville" value={filters.ville} onChange={(v) => setFilters({ ...filters, ville: v })} testid="filter-ville" />
          <FilterDate label="Visite du" value={filters.date_visite_from} onChange={(v) => setFilters({ ...filters, date_visite_from: v })} testid="filter-date-from" />
          <FilterDate label="Visite au" value={filters.date_visite_to} onChange={(v) => setFilters({ ...filters, date_visite_to: v })} testid="filter-date-to" />
          <div className="flex items-end">
            <button data-testid="filters-reset" onClick={() => setFilters(EMPTY_FILTERS)} className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50">
              <X className="h-3 w-3" /> Réinitialiser
            </button>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="hidden cvt-scroll overflow-x-auto lg:block">
          <table data-testid="rdv-table" className="w-full min-w-[1100px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {cols.map((c) => (
                  <th key={c.key} className="whitespace-nowrap px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    <button data-testid={`sort-${c.key}`} onClick={() => toggleSort(c.key)} className="flex items-center gap-1 hover:text-slate-900">
                      {c.label} <SortIcon k={c.key} />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={cols.length} className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-300" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={cols.length} className="py-16 text-center text-slate-400">Aucun résultat</td></tr>
              ) : rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`rdv-row-${r.id}`}
                  onClick={() => navigate(`/visites/${r.id}`)}
                  className="cursor-pointer border-b border-slate-100 transition-colors duration-150 hover:bg-blue-50/40"
                >
                  <td className="px-3 py-2.5"><span className="mono text-xs font-semibold text-amber-600">{r.numero_dossier}</span></td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-xs text-slate-500">{formatDateTime(r.date_creation)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-medium text-slate-900">{r.client_nom}</td>
                  <td className="whitespace-nowrap px-3 py-2.5"><span className="mono text-xs text-slate-600">{r.client_telephone}</span></td>
                  <td className="px-3 py-2.5"><span className="mono text-xs text-slate-600">{r.code_postal}</span></td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-xs text-slate-600">{r.date_visite ? formatDate(r.date_visite) : "—"} {r.heure_debut ? <span className="mono text-slate-400">{r.heure_debut}</span> : ""}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-700">{r.technicien_nom || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2.5"><StatutBadge code={r.statut} color={r.statut_color} label={r.statut_label} /></td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-xs text-slate-600">{r.type_visite}</td>
                  {canFilterInternal && <td className="px-3 py-2.5">{r.origine === "NOVASOL" ? <span className="rounded-md bg-indigo-100 px-2 py-0.5 text-[10px] font-bold uppercase text-indigo-700">NovaSol</span> : <span className="text-xs text-slate-400">Partenaire</span>}</td>}
                  <td className="px-3 py-2.5">{r.nb_documents > 0 ? <span className="flex items-center gap-1 text-xs text-slate-500"><FileText className="h-3.5 w-3.5" />{r.nb_documents}</span> : <span className="text-slate-300">—</span>}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-xs text-slate-500">{r.createur_nom}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-xs text-slate-500">{r.unite_affaire}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="divide-y divide-slate-100 lg:hidden" data-testid="rdv-mobile-list">
          {loading ? (
            <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-300" /></div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-slate-400">Aucun résultat</div>
          ) : rows.map((r) => (
            <div key={r.id} data-testid={`rdv-card-${r.id}`} onClick={() => navigate(`/visites/${r.id}`)} className="cursor-pointer p-4 active:bg-blue-50/40">
              <div className="flex items-center justify-between gap-2">
                <span className="mono text-xs font-semibold text-amber-600">{r.numero_dossier}</span>
                <StatutBadge code={r.statut} color={r.statut_color} label={r.statut_label} />
              </div>
              <div className="mt-1 font-semibold text-slate-900">{r.client_nom}</div>
              <div className="mt-0.5 text-xs text-slate-500">{r.type_visite} · {r.code_postal} {r.ville}</div>
              <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                <span>{r.date_visite ? formatDate(r.date_visite) : "Non planifié"} {r.heure_debut ? <span className="mono">{r.heure_debut}</span> : ""}</span>
                <span>{r.technicien_nom || "—"}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
          <span className="text-xs text-slate-500">Page {page} / {pages}</span>
          <div className="flex gap-2">
            <button data-testid="page-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
            <button data-testid="page-next" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {showForm && <VisiteFormModal mode="create" onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); toast.success("Rendez-vous créé"); }} />}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm outline-none focus:border-slate-400">
        <option value="">Tous</option>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

function FilterInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</label>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm outline-none focus:border-slate-400" />
    </div>
  );
}

function FilterDate({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</label>
      <DatePicker value={value} onChange={onChange} testid={testid} />
    </div>
  );
}
